// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/DayChips.tsx
// 🔧 PURPOSE: LA SCELTA DEL GIORNO FRA QUELLI AMMESSI, E BASTA. Un
//             selettore nativo con min/max su iPhone lascia scegliere
//             qualunque data; qui i giorni possibili sono chip ("lun 15
//             set"), uno per giorno, e non ce ne sono altri. Serve
//             all'escursione (un giorno del soggiorno), al transfer in
//             aereo (arrivo: il giorno del check-in o il successivo;
//             partenza: il giorno del check-out o il successivo).
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../lib/locale';

/** "YYYY-MM-DD" → mezzanotte locale. */
export const parseDayKey = (day: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** Il giorno di OGGI in Italia ("YYYY-MM-DD"): i giorni del soggiorno sono giorni di Roma, non del telefono. */
export const romeToday = (d: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);

export const dayKeyOf = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Tutti i giorni da `from` a `to` compresi (chiavi "YYYY-MM-DD"), al massimo `max`. */
export const dayRange = (from: string, to: string, max = 31): string[] => {
  const a = parseDayKey(from);
  const b = parseDayKey(to);
  if (!a || !b) return [];
  const out: string[] = [];
  for (let d = new Date(a); d.getTime() <= b.getTime() && out.length < max; d.setDate(d.getDate() + 1)) {
    out.push(dayKeyOf(d));
  }
  return out;
};

export interface DayChipsProps {
  days: string[];
  value?: string;
  onChange: (day: string) => void;
  /** Il giorno di oggi, per scrivere "oggi"/"domani" al posto della data. */
  today?: string;
  disabled?: boolean;
}

const DayChips: React.FC<DayChipsProps> = ({ days, value, onChange, today, disabled }) => {
  const { t, i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(intlLocale(i18n.language), { weekday: 'short', day: 'numeric', month: 'short' });
  const tomorrow = today ? (() => { const d = parseDayKey(today); if (!d) return ''; d.setDate(d.getDate() + 1); return dayKeyOf(d); })() : '';
  if (days.length === 0) return null;
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{ WebkitOverflowScrolling: 'touch' }}>
      {days.map((day) => {
        const on = value === day;
        const d = parseDayKey(day);
        const label = day === today ? t('common.today') : day === tomorrow ? t('common.tomorrow') : d ? fmt.format(d) : day;
        return (
          <button
            key={day}
            type="button"
            disabled={disabled}
            onClick={() => onChange(day)}
            className={`flex-shrink-0 px-3.5 py-2 rounded-full text-[14px] font-semibold border-2 transition-colors disabled:opacity-40 ${
              on
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
                : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default DayChips;
